import React, { Suspense, useCallback, useContext } from "react";
import PropTypes from "prop-types";
import { connect, useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useMediaQuery } from "@mui/system";

import { LogoTypeContext } from "../../containers/FormLayout/LogoTypeProvider";
import { ReactComponent as ExitIcon } from "../../assets/icons/Exit_door.svg";
import routes from "../../routes";

import { LOGO_ELITE, LOGO_ELITE_ISLAMIC, LOGO_ISLAMIC, LOGO_STANDART } from "./constants";

import { ReactComponent as EliteIslamicLogo } from "../../assets/images/logo-elite-islamic.svg";
import { ReactComponent as StandartLogo } from "../../assets/images/logo-standart.svg";
import { ReactComponent as EliteLogo } from "../../assets/images/logo-elite.svg";
import { ReactComponent as IslamicLogo } from "../../assets/images/logo-islamic-new.svg";

import { HeaderContainer, StyledHeader, StyledHeaderButton } from "./styled";
import { LayoutContext } from "../../containers/FormLayout/LayoutProvider";
import { getIsIslamicBanking, getProspectId } from "../../store/selectors/appConfig";
import { useTrackingHistory } from "../../utils/useTrackingHistory";
import { checkLoginStatus } from "../../store/selectors/loginSelector";
import HeaderProgressBar from "../ProgressBar/ProgressBarContainer";
import SaveAndClose from "../SaveAndClose";
import { TrackNSwitchAccountBtn } from "../../commonStyles";
import { resetProspect } from "../../store/actions/appConfig";
import { getIsEditableStatusSearchInfo } from "../../store/selectors/searchProspect";

// Custom Exit Button Component
/**
 * ExitButton renders a styled button with an icon and text for exit actions.
 *
 * @component
 * @param {Object} props - Component props
 * @param {React.ElementType} props.icon - Icon component to render
 * @param {string} props.text - Button text
 * @param {function} props.onClick - Click handler
 * @returns {JSX.Element}
 */
const ExitButton = ({ icon: Icon, text, onClick }) => (
  <button
    onClick={onClick}
    style={{
      background: "transparent",
      border: "none",
      color: "white",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "8px 12px",
      cursor: "pointer",
      fontSize: "14px",
      fontWeight: "500"
    }}
  >
    <Icon style={{ width: "20px", height: "20px", fill: "white" }} />
    {text}
  </button>
);

ExitButton.propTypes = {
  /** Icon component to render */
  icon: PropTypes.elementType.isRequired,
  /** Button text */
  text: PropTypes.string.isRequired,
  /** Click handler */
  onClick: PropTypes.func.isRequired
};

/**
 * HeaderComponent displays the application header with logo, progress bar, and action buttons.
 * Handles navigation, logo selection, and conditional rendering for agent/RO flows.
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.isAgent - Whether the user is an agent (from Redux)
 * @returns {JSX.Element}
 */
const HeaderComponent = ({ isAgent }) => {
  const { pathname } = useLocation();
  const queryParams = useLocation().search;
  const isIslamic = useSelector(getIsIslamicBanking);
  const pushHistory = useTrackingHistory();
  const isDesktop = useMediaQuery("(min-width: 769px)") || window.innerWidth > 769;
  const logoType = useContext(LogoTypeContext);
  const [, , , showSaveNClose = false, showResumeBtn = false] = useContext(LayoutContext);
  const showResumeButton =
    pathname === "/business/finances" || pathname.includes("application-overview");

  const handleRedirection = (path, replaceHistory = false) => {
    pushHistory(path, replaceHistory);
  };

  const dispatch = useDispatch();
  const navigate = useNavigate(); // Changed from useHistory
  const prospectId = useSelector(getProspectId);
  const isComeFromROScreens = useSelector(getIsEditableStatusSearchInfo);

  const exitSalesAssist = useCallback(() => {
    dispatch(resetProspect());
    navigate(routes.searchProspect); // Changed from history.push
  }, [dispatch, navigate]); // Updated dependencies

  return (
    // <>
    <HeaderContainer logoType={logoType} isIslamic={isIslamic}>
      <StyledHeader>
        <Link to={routes.financeProductComparison + queryParams}>
          {(() => {
            switch (logoType) {
              case LOGO_ELITE_ISLAMIC:
                return <EliteIslamicLogo className="logo" />;
              case LOGO_ELITE:
                return <EliteLogo className="logo" />;
              case LOGO_ISLAMIC:
                return <IslamicLogo className="logo" />;
              case LOGO_STANDART:
              default:
                return <StandartLogo className="logo" />;
            }
          })()}
        </Link>
        {isDesktop && <HeaderProgressBar />}
        <div className="headerAction">
          {showResumeButton && (
            <TrackNSwitchAccountBtn
              style={{ background: "white", text: "black", border: "white" }}
              onClick={() => handleRedirection(routes.comeBackLogin)}
            >
              Resume
            </TrackNSwitchAccountBtn>
          )}
          {showSaveNClose && <SaveAndClose extraClasses="saveNCloseHeader" />}
          {showResumeBtn && (
            <StyledHeaderButton onClick={() => handleRedirection(routes.comeBackLogin)}>
              Resume
            </StyledHeaderButton>
          )}
          {isAgent && prospectId && isComeFromROScreens && (
            <div style={{ background: "transparent" }}>
              <Suspense fallback={<div>Loading...</div>}>
                <ExitButton icon={ExitIcon} text="Exit Sales Assist" onClick={exitSalesAssist} />
              </Suspense>
            </div>
          )}
        </div>
      </StyledHeader>
    </HeaderContainer>
  );
};

HeaderComponent.propTypes = {
  /** Whether the user is an agent (from Redux) */
  isAgent: PropTypes.bool.isRequired
};

const mapStateToProps = state => ({
  isAgent: checkLoginStatus(state)
});

export const Header = connect(mapStateToProps)(HeaderComponent);
